import React, { Fragment, useContext, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import FadeIn from 'react-fade-in';
import { toast } from 'react-toastify';
import momentLocalizer from 'react-widgets-moment';
import { GlobalHotKeys } from 'react-hotkeys';
import moment from 'moment';

import BodyEnd from '../../_shared/BodyEnd';
import ConfirmModal from '../../_shared/ConfirmModalComponent';
import QuickDrawerHeader from '../../_shared/QuickDrawerHeader';
import RichTextEditor from '../../_shared/RichTextEditor';

import GroupAppointmentUpdateStore from '../../../../stores/GroupAppointmentUpdateStore';
import QuickDrawerStore from '../../../../stores/QuickDrawerStore';

import { quickDrawerFocus, renderQuickDrawerLoading } from '../../_shared/QuickDrawer';
import * as rts from '../../../../constants/routes';
import * as ErrorMessages from '../../../../constants/errorMessages';
import * as fn from '../../../../utilities/_functions';
import * as ah from '../../../../utilities/addressHelper';
import * as ch from '../../../../utilities/customerHelper';
import * as aph from '../../../../utilities/appointmentHelper';
import * as bh from '../../../../utilities/badgeHelper';
import * as ph from '../../../../utilities/personHelper';
import * as sys from '../../../../utilities/systemHelper';
import * as oh from '../../../../utilities/operationHelper';

import './DeleteGroupAppointment.scss';

moment.locale('en');
momentLocalizer();

function DeleteGroupAppointment(props) {
    const isMounted = useRef(true);
    const validateRef = useRef(null);
    const confirmModalRef = useRef(null);
    const groupAppointment = useContext(GroupAppointmentUpdateStore);
    const quickDrawer = useContext(QuickDrawerStore);

    useEffect(() => {
        quickDrawerFocus(props.drawer);

        return () => {
            isMounted.current = false;
        }
    }, []) // eslint-disable-line

    const handleCancel = () => {
        if (fn.isFunction(props.onCancel)) {
            if (groupAppointment.hasUnsavedChanges) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onCancel();
                }
            } else {
                props.onCancel();
            }
        }
    }

    const handleRequestByChange = event => {
        groupAppointment.cancelRequestBy = event.target.value;
        groupAppointment.hasUnsavedChanges = true;
    }

    const handleReasonChange = (content, delta, source, editor) => {
        const reason = editor.getText().replace(/(\r\n|\n|\r)/g, ' ').trim();
        const reasonHtml = (content === '<p><br></p>') ? null : content;

        groupAppointment.cancelRequestReason = reason;
        groupAppointment.cancelRequestReasonHtml = reasonHtml;
        groupAppointment.hasUnsavedChanges = true;
    }

    const handleSubmit = event => {
        event.preventDefault();
        if (isMounted.current) {
            if (fn.validateForm(validateRef.current)) {
                confirmModalRef.current.show({
                    icon: <i className='fal fa-calendar-times text-danger mr-2'></i>,
                    message: <>Continue to <span data-continue-delete-appointment className='fw-500'>&nbsp;delete this group appointment</span>? </>,
                    onOption1Click: handleConfirmDelete,
                    onCancel: handleConfirmCancel,
                })
            }
        }
    }

    const handleConfirmDelete = event => {
        event.preventDefault();
        groupAppointment.delete()
            .then(data => {
                if (isMounted.current) {
                    if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                        const data = toJS(groupAppointment.data);
                        props.onSuccess(event, { updated: true, data: data });
                    }
                }
            })
            .catch(error => {
                const msg = <p><strong>Cannot cancel appointment.</strong>{error && error.response && error.response.data ? <>&nbsp;{error.response.data}</> : null}</p>;
                toast.error(() => msg, { position: 'top-center', style: { width: '380px' } });
            })

        confirmModalRef.current.close();
    }

    const handleConfirmCancel = event => {
        event.preventDefault();
        confirmModalRef.current.close();

        if (props.onCancel && fn.isFunction(props.onCancel)) {
            props.onCancel(event);
        }
    }

    const renderAppointmentDateTime = () => {
        if (!groupAppointment.data.start) return null;

        const start = moment(groupAppointment.data.start);
        const end = groupAppointment.data.end ? moment(groupAppointment.data.end) : null;
        const hasEndTime = !!end;
        const weekday = start.format('dddd');
        const day = start.format('D');
        const ordinal = start.format('Do').replace(day, '');
        const dateHtml = `${start.format('MMMM D')}<sup>${ordinal}</sup>${((start.year() !== moment().year()) ? `, ${start.format('YYYY')}` : '')}`;
        const startTimeHtml = `${start.format('h:mm')}${(!hasEndTime || start.format('a') !== end.format('a') ? ` ${start.format('a')}` : '')}`;
        const endTimeHtml = hasEndTime ? `${end.format('h:mm')} ${end.format('a')}` : '';

        return <ul className='list-inline no-style m-0'>
            <li className='list-inline-item m-0'>
                <div className='text'>
                    <small className='weekday'>{weekday}</small>
                    <span className='date' dangerouslySetInnerHTML={{ __html: dateHtml }}></span>
                </div>
            </li>
            <li className='list-inline-item my-0 mx-1'><small>@</small></li>
            <li className='list-inline-item m-0'>
                <div className='text'>
                    <span className='time' dangerouslySetInnerHTML={{ __html: startTimeHtml }}></span>
                </div>
            </li>
            <li className='list-inline-item my-0 mx-1'><small>to</small></li>
            <li className='list-inline-item m-0'>
                <div className='text'>
                    <span className='time' dangerouslySetInnerHTML={{ __html: endTimeHtml }}></span>
                </div>
            </li>
        </ul>
    }

    const renderResource = () => {
        const { resource } = groupAppointment.data;

        return <div
            className='profile-wrapper'
        >
            <div className='profile'>
                <span
                    className={`profile-image rounded-circle d-block fw-500` + (resource && !resource.profilePictureUri ? ` profile-initials bg-color${resource.color}-500` : '')}
                    style={resource && resource.profilePictureUri ? {
                        backgroundImage: `url(${resource.profilePictureUri})`,
                        backgroundSize: 'cover',
                    } : null}
                    title={resource ? resource.fullName : 'System'}>
                    {resource && !resource.profilePictureUri ? <div className='d-initials'>{resource.initials}</div> : null}
                </span>
            </div>
            <div className='description'>
                <div className='name'>{resource.fullName}</div>
                <div className='services'>
                    {bh.renderAppointmentNew(groupAppointment.data, 'mr-1')}
                    {bh.renderAppointmentFirstExam(groupAppointment.data, 'mr-1')}
                    {
                        groupAppointment.data.services.map((s, si) => {
                            return <Fragment
                                key={`service_${si}`}
                            >
                                {bh.renderServiceCode(s, 'p-1 mr-1', (!s.isEligible || !aph.isEndState(groupAppointment.data)))}
                            </Fragment>
                        })
                    }
                    {bh.renderAppointmentRescheduledByOffice(groupAppointment.data, 'mr-1')}
                    {bh.renderAppointmentRescheduledByCustomer(groupAppointment.data, 'mr-1')}
                </div>
            </div>
        </div>
    }

    const renderPrimaryContact = () => {
        if (!groupAppointment.data) return;

        const { primaryContact } = groupAppointment.data;

        return <div
            className='profile-wrapper pr-0 pb-0'
        >
            <div className='profile'>
                <span
                    className={`profile-image profile-initials rounded-circle d-flex text-white ${ch.getProfileBgColor(primaryContact)} fw-500`}
                    title={ph.getFullName(primaryContact)}
                >
                    {primaryContact.initials}
                </span>
            </div>
            <div className='description flex-1'>
                <Link
                    to={`${rts.Customers.View}/${primaryContact.id}`}
                    className='name text-gray-700 text-info-hover'
                    onClick={quickDrawer.deactivateAll}
                >
                    {ph.getFullName(primaryContact, true)}
                    {
                        primaryContact.dateOfBirth || primaryContact.sex || primaryContact.gender || primaryContact.pronoun ?
                            <small className='text-nowrap ml-2'>({`${ph.getAge(primaryContact.dateOfBirth, moment(groupAppointment.data.start))} ${ph.getSexGenderPronounDisplay(primaryContact)}`.trim()})</small> : null
                    }
                </Link>
                {
                    primaryContact.address && primaryContact.address.country ?
                        <div className='info'>{ah.getAddressHtml(primaryContact.address)}</div> : null
                }
                {
                    primaryContact.emailAddress ?
                        <div className='info'>
                            <a
                                href={`mailto:${primaryContact.emailAddress}`}
                            >{primaryContact.emailAddress}
                            </a>
                        </div> : null
                }
                {
                    primaryContact.phoneNumber ?
                        <div className='info'>
                            <a
                                href={`tel:${primaryContact.phoneNumber}`}
                            >{sys.getFormattedPhoneNumber(primaryContact.phoneNumber)}
                            </a>
                        </div> : null
                }
            </div>
        </div>
    }

    const renderAppointment = (appointment) => {
        if (!appointment.customer) return;

        const { customer } = appointment;

        return <div
            className='profile-wrapper pr-0'
        >
            <div className='profile'>
                <span
                    className={`profile-image profile-initials rounded-circle d-flex text-white ${ch.getProfileBgColor(customer)} fw-500`}
                    title={ph.getFullName(customer)}
                >
                    {customer.initials}
                </span>
            </div>
            <div className='description flex-1'>
                <Link
                    to={`${rts.Customers.View}/${customer.id}`}
                    className='name text-gray-700 text-info-hover'
                    onClick={quickDrawer.deactivateAll}
                >
                    {ph.getFullName(customer, true)}
                    {
                        customer.dateOfBirth || customer.sex || customer.gender || customer.pronoun ?
                            <small className='text-nowrap ml-2'>({`${ph.getAge(customer.dateOfBirth, moment(groupAppointment.data.start))} ${ph.getSexGenderPronounDisplay(customer)}`.trim()})</small> : null
                    }
                </Link>
                {
                    customer.dateOfBirth ?
                        <div className='info text-gray-700'><span className='text-nowrap mr-1'><strong className='mr-1'>DOB:</strong>{sys.getFormattedLongDate(customer.dateOfBirth)}</span></div> : null
                }
                {
                    customer.patientProfile && customer.patientProfile.healthCardNumber ?
                        <div className='info text-gray-700'><span className='text-nowrap mr-1'><strong className='mr-1'>{ph.getHealthCardNumberLabel().shortName}:</strong>{ph.formatHealthCard(customer.patientProfile.healthCardNumber)}</span>{ph.hasHealthCardExpiry() ? <small className={'text-nowrap'}>(Exp. {fn.formatDate(customer.patientProfile.healthCardExpiryDate, 'MMM Do, YYYY')})</small> : <></>}</div> : null
                }
                <div className='py-1'>
                    {bh.renderAppointmentNew(appointment, 'p-1 mr-1')}
                    {bh.renderAppointmentFirstExam(appointment, 'p-1 mr-1')}
                    {
                        appointment.services.map((s, si) => {
                            return <Fragment
                                key={`service_${si}`}
                            >
                                {bh.renderServiceCode(s, 'p-1 mr-1', (!s.isEligible || !aph.isEndState(appointment)))}
                            </Fragment>
                        })
                    }
                </div>
            </div>
        </div>
    }

    return <>
        <form ref={validateRef} onSubmit={handleSubmit}>
            <Observer>{() =>
                <>
                    {
                        (props.drawer === quickDrawer.drawerOpened) && (confirmModalRef.current && !confirmModalRef.current.isVisible) ?
                            <GlobalHotKeys
                                keyMap={{
                                    close: ['esc'],
                                }}
                                handlers={{
                                    close: event => {
                                        handleCancel(event)
                                    },
                                }}
                                allowChanges={true}
                            /> : null
                    }
                    <fieldset disabled={groupAppointment.isSaving}>
                        <div className='quick-drawer'>
                            <QuickDrawerHeader
                                drawer={props.drawer}
                                icon={oh.getIcon('appointment', 'delete')}
                                action='Delete'
                                category='Group Appointment'
                                className='delete'
                                onCancel={handleCancel}
                            />
                            <div className='quick-drawer-body'>
                                {
                                    groupAppointment.isReady ?
                                        <FadeIn>
                                            <div className='delete-group-appointment body-content'>
                                                <section className='date-time'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            {renderAppointmentDateTime()}
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='resource'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <div className='form-group mb-0'>
                                                                <label><small>With</small></label>
                                                                {renderResource()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='customer'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <div className='form-group mb-0'>
                                                                <label><small>Primary Contact</small></label>
                                                                {renderPrimaryContact()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='customer'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <div className='form-group mb-0'>
                                                                <label><small>For</small></label>
                                                                {
                                                                    groupAppointment.appointments && groupAppointment.appointments.length > 0 ?
                                                                        groupAppointment.appointments.slice().sort((a, b) => {
                                                                            if (a.customer.id === a.primaryContactId) {
                                                                                return 1;
                                                                            }
                                                                            else if (a.customer.dateOfBirth && b.customer.dateOfBirth) {
                                                                                return moment(a.customer.dateOfBirth).isBefore(moment(b.customer.dateOfBirth)) ? -1 : 1;
                                                                            }
                                                                            else {
                                                                                return a.customer.firstName - b.customer.firstName;
                                                                            }
                                                                        }).map((a, ai) => {
                                                                            return <div key={`group_group_appointment_${ai}`} className='mt-2'>
                                                                                {renderAppointment(a)}
                                                                            </div>
                                                                        }) : null
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <Observer>{() =>
                                                                <div className={'form-group validate validate-required mb-0'}>
                                                                    <label className='required'><small>Cancel by</small></label>
                                                                    <div className='custom-control custom-radio mb-2'>
                                                                        <input
                                                                            id='requestByCustomer'
                                                                            type='radio'
                                                                            name='requestBy'
                                                                            value='Customer'
                                                                            className='custom-control-input'
                                                                            checked={groupAppointment.cancelRequestBy === 'Customer'}
                                                                            onChange={handleRequestByChange}
                                                                        />
                                                                        <label
                                                                            htmlFor='requestByCustomer'
                                                                            className='custom-control-label'
                                                                        >
                                                                            {
                                                                                groupAppointment.originalData ?
                                                                                    <>{ph.getFullName(groupAppointment.originalData.primaryContact, true)} <span className='text-smaller text-gray-700'>(Patient)</span></> : <>Patient</>
                                                                            }
                                                                        </label>
                                                                    </div>
                                                                    <div className='custom-control custom-radio mb-2'>
                                                                        <input
                                                                            id='requestByResource'
                                                                            type='radio'
                                                                            name='requestBy'
                                                                            value='Resource'
                                                                            className='custom-control-input'
                                                                            checked={groupAppointment.cancelRequestBy === 'Resource'}
                                                                            onChange={handleRequestByChange}
                                                                        />
                                                                        <label
                                                                            htmlFor='requestByResource'
                                                                            className='custom-control-label'
                                                                        >
                                                                            {
                                                                                groupAppointment.originalData ?
                                                                                    <>{ph.getFullName(groupAppointment.originalData.resource, true)} <span className='text-smaller text-gray-700'>(Our office)</span></> : <>Doctor</>
                                                                            }
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            }</Observer>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <div className={'form-group'}>
                                                                <label><small>Cancel Reason</small></label>
                                                                <div className='input-group'>
                                                                    <Observer>{() =>
                                                                        <RichTextEditor
                                                                            mode='basic'
                                                                            value={groupAppointment.cancelRequestReasonHtml}
                                                                            disableTab={true}
                                                                            onChange={handleReasonChange}
                                                                        />
                                                                    }</Observer>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </section>
                                            </div>
                                        </FadeIn> : renderQuickDrawerLoading()
                                }
                            </div>
                            <Observer>{() =>
                                groupAppointment.isReady ?
                                    <div className='quick-drawer-action'>
                                        <div className='row'>
                                            <div className='col-12'>
                                                <div className='float-right'>
                                                    <button
                                                        type='button'
                                                        className='btn btn-link btn-cancel mr-2'
                                                        onClick={props.onCancel}
                                                    >Cancel</button>
                                                    <Observer>{() =>
                                                        <button
                                                            data-delete-appointment
                                                            type='submit'
                                                            className='btn btn-danger'
                                                        >Delete</button>
                                                    }</Observer>
                                                </div>
                                            </div>
                                        </div>
                                    </div> : null
                            }</Observer>
                        </div>
                    </fieldset>
                </>
            }</Observer>
        </form>
        <BodyEnd>
            <ConfirmModal ref={confirmModalRef} />
        </BodyEnd>
    </>
}

export default DeleteGroupAppointment;